import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useNotifications } from '@/lib/friends';
import { usePrivacy } from '@/lib/privacy';
import { api } from '@/lib/api';
import s from './NotificationBell.module.css';
export function NotificationBell() {
  const query = useNotifications(),
    client = useQueryClient(),
    navigate = useNavigate(),
    { masked } = usePrivacy();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', key);
    };
  }, [open]);
  async function read(id?: string) {
    try {
      await api('/friends/notifications/read', {
        method: 'POST',
        body: id ? { id } : {},
      });
      await client.invalidateQueries({ queryKey: ['notifications'] });
    } catch {
      /* The notification stays unread and can be retried. */
    }
  }
  return (
    <div ref={root} className={s.root}>
      <button
        className={s.bell}
        type="button"
        aria-label={`Notifications${query.data?.unread ? ` (${query.data.unread} unread)` : ''}`}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Bell size={20} aria-hidden="true" />
        {!!query.data?.unread && (
          <span className={s.badge}>{query.data.unread > 99 ? '99+' : query.data.unread}</span>
        )}
      </button>
      {open && (
        <div className={s.panel} role="region" aria-label="Notifications">
          <div className={s.head}>
            <strong>Notifications</strong>
            <button type="button" onClick={() => void read()}>
              Mark all read
            </button>
          </div>
          {query.error ? (
            <p>Couldn’t load notifications. Try again shortly.</p>
          ) : !query.data?.notifications.length ? (
            <p>You’re all caught up.</p>
          ) : (
            query.data.notifications.map((notice) => (
              <button
                key={notice.id}
                className={`${s.notice} ${!notice.readAt ? s.unread : ''}`}
                type="button"
                onClick={() => {
                  void read(notice.id);
                  setOpen(false);
                  navigate(notice.href);
                }}
              >
                <strong>{notice.title}</strong>
                <span>
                  {masked ? 'Reveal financial details to read this update.' : notice.body}
                </span>
                <time>{new Date(notice.createdAt).toLocaleDateString()}</time>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
